/** @jsxImportSource @emotion/react */
import { TextField, Box } from "@mui/material"
import { useRef, useState, useEffect } from "react"
import { withMyTheme } from "../../../../theme/theme"
import { css } from "@mui/material"

const TextFieldStyle = withMyTheme((theme) => css`
    width: 100%;
    
    & .MuiInputLabel-root {
        font-family: ${theme.typography.body1.fontFamily};
        color: ${theme.palette.text.primary};
        font-size: 1rem;
        background: white;
        padding: 0 6px;
        
        &.Mui-focused {
            color: ${theme.palette.primary.main};
        }
    }
    
    & .MuiOutlinedInput-root {
        border-radius: 12px;
        background-color: white;
        
        &:hover .MuiOutlinedInput-notchedOutline {
            border-color: ${theme.palette.primary.main};
        }
        
        &.Mui-focused .MuiOutlinedInput-notchedOutline {
            border-color: ${theme.palette.primary.main};
            border-width: 2px;
        }
    }
    
    & .MuiInputBase-input {
        font-family: ${theme.typography.body1.fontFamily};
        font-size: 1rem;
        padding: 14px 16px;
    }
`)

interface LocationAutocompleteProps {
    label: string
    placeholder: string
    value: string
    onChange: (location: string) => void
}

export const LocationAutocomplete = ({ 
    label, 
    placeholder, 
    value, 
    onChange 
}: LocationAutocompleteProps) => {
    const inputRef = useRef<HTMLInputElement>(null)
    const [isLoaded, setIsLoaded] = useState(false)
    const [autocomplete, setAutocomplete] = useState<google.maps.places.Autocomplete | null>(null)

    useEffect(() => {
        // Check if Google Maps script is already loaded
        if (window.google && window.google.maps && window.google.maps.places) {
            setIsLoaded(true)
            return
        }

        // Load Google Maps script
        const script = document.createElement('script')
        const apiKey = process.env.REACT_APP_GOOGLE_PLACES_API_KEY || ''
        script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places&loading=async`
        script.async = true
        script.defer = true
        
        script.onload = () => {
            setIsLoaded(true)
        }
        
        script.onerror = () => {
            console.error('Failed to load Google Maps script')
            setIsLoaded(true) // Set to true anyway to show fallback input
        }

        document.head.appendChild(script)

        return () => {
            // Cleanup script on unmount if needed
            const existingScript = document.querySelector(`script[src^="https://maps.googleapis.com/maps/api/js"]`)
            if (existingScript && existingScript === script) {
                document.head.removeChild(script)
            }
        }
    }, [])

    useEffect(() => {
        if (!isLoaded || !inputRef.current || !window.google?.maps?.places) {
            return
        }

        // Initialize autocomplete
        const autocompleteInstance = new window.google.maps.places.Autocomplete(
            inputRef.current,
            {
                fields: ['formatted_address', 'name', 'geometry'],
            }
        )

        // Add place changed listener
        autocompleteInstance.addListener('place_changed', () => {
            const place = autocompleteInstance.getPlace()
            if (place.formatted_address) {
                onChange(place.formatted_address)
            } else if (place.name) {
                onChange(place.name)
            }
        })

        setAutocomplete(autocompleteInstance)

        return () => {
            if (autocompleteInstance && window.google?.maps?.event) {
                try {
                    window.google.maps.event.clearInstanceListeners(autocompleteInstance)
                } catch (error) {
                    // Silently catch any errors during cleanup
                    console.debug('Error clearing autocomplete listeners:', error)
                }
            }
        }
    }, [isLoaded])

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        onChange(event.target.value)
    }

    return (
        <Box sx={{ width: '100%' }}>
            <TextField
                inputRef={inputRef}
                label={label}
                placeholder={placeholder}
                value={value}
                onChange={handleInputChange}
                fullWidth
                variant="outlined"
                disabled={!isLoaded}
                InputLabelProps={{
                    shrink: true,
                }}
                css={TextFieldStyle}
            />
        </Box>
    )
}
